import React from 'react'
import { Button, Popconfirm, Space, Tag, Typography } from 'antd'
import {
  SettingOutlined, ApiOutlined, DeleteOutlined,
  CheckCircleOutlined, CloseCircleOutlined, MinusCircleOutlined,
} from '@ant-design/icons'
import { AppCard } from '@/shared/components/ui'
import ConnectorStatusBadge from './ConnectorStatusBadge'
import type { ConnectorConfig, ConnectorStatus } from '../types'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

const { Text, Title } = Typography

interface Props {
  config: ConnectorConfig
  status?: ConnectorStatus
  onEdit: (config: ConnectorConfig) => void
  onDelete: (provider: string) => void
  onTest: (config: ConnectorConfig) => void
}

const DOMAIN_LABELS: Record<string, string> = {
  product: 'Product',
  sales: 'Sales',
  inventory: 'Inventory',
  category: 'Category',
  customer: 'Customer',
}

const ALL_DOMAINS = ['product', 'sales', 'inventory', 'category', 'customer']

const ConnectorCard: React.FC<Props> = ({ config, status, onEdit, onDelete, onTest }) => {
  const connectorStatus = status?.status || 'configured'
  const enabledDomains = new Set(config.enabled_domains || [])

  return (
    <AppCard
      style={{
        width: '100%',
        borderLeft: connectorStatus === 'connected' ? '3px solid #52c41a' :
                     connectorStatus === 'error' ? '3px solid #ff4d4f' : '3px solid #d9d9d9'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <Title level={5} style={{ margin: 0 }}>
            <ApiOutlined style={{ marginRight: 8 }} />
            {config.provider.charAt(0).toUpperCase() + config.provider.slice(1)}
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>{config.base_url}</Text>
        </div>
        <ConnectorStatusBadge status={connectorStatus} />
      </div>

      {/* Domains */}
      <div style={{ marginBottom: 12 }}>
        {ALL_DOMAINS.map(domain => (
          <Tag
            key={domain}
            icon={enabledDomains.has(domain) ? <CheckCircleOutlined /> : <MinusCircleOutlined />}
            color={enabledDomains.has(domain) ? 'blue' : undefined}
            style={{ marginBottom: 4 }}
          >
            {DOMAIN_LABELS[domain] || domain}
          </Tag>
        ))}
      </div>

      {/* Last test */}
      {config.last_tested_at && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Last tested: {dayjs(config.last_tested_at).fromNow()}
            {config.test_result === true && <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 4 }} />}
            {config.test_result === false && <CloseCircleOutlined style={{ color: '#ff4d4f', marginLeft: 4 }} />}
          </Text>
        </div>
      )}

      {status?.latency_ms !== undefined && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Latency: {status.latency_ms}ms</Text>
        </div>
      )}

      {/* Actions */}
      <Space>
        <Button size="small" icon={<SettingOutlined />} onClick={() => onEdit(config)}>
          Configure
        </Button>
        <Button size="small" icon={<ApiOutlined />} onClick={() => onTest(config)} loading={false}>
          Test
        </Button>
        <Popconfirm
          title="Delete this connector?"
          description="This will remove the configuration permanently."
          onConfirm={() => onDelete(config.provider)}
          okText="Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <Button size="small" icon={<DeleteOutlined />} danger>
            Delete
          </Button>
        </Popconfirm>
      </Space>
    </AppCard>
  )
}

export default ConnectorCard
